/** XeAgents.ai **/
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { Save, Loader2, AlertCircle, Database, HardDrive } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InfoTooltip } from "@/components/ui/info-tooltip";

interface RAGSettings {
  rag_provider: string;
  aws_region: string;
  bedrock_region: string;
  aws_access_key_id: string;
  aws_secret_access_key: string;
  bedrock_embedding_model: string;
  bedrock_reranking_model: string;
  openai_api_key: string;
  s3_recording_bucket: string;
}

export default function RAGSettings() {
  const { toast } = useToast();
  const [formData, setFormData] = useState<Partial<RAGSettings>>({
    rag_provider: "openai",
    bedrock_embedding_model: "amazon.titan-embed-text-v2:0"
  });
  const [hasChanges, setHasChanges] = useState(false);

  const { data: settings, isLoading } = useQuery<RAGSettings>({
    queryKey: ["/api/admin/settings"]
  });

  useEffect(() => {
    if (settings) {
      setFormData({
        rag_provider: settings.rag_provider || "openai",
        aws_region: settings.aws_region || "",
        bedrock_region: settings.bedrock_region || "us-east-1",
        aws_access_key_id: settings.aws_access_key_id || "",
        aws_secret_access_key: settings.aws_secret_access_key || "",
        bedrock_embedding_model: settings.bedrock_embedding_model || "amazon.titan-embed-text-v2:0",
        bedrock_reranking_model: settings.bedrock_reranking_model || "",
        openai_api_key: settings.openai_api_key || "",
        s3_recording_bucket: settings.s3_recording_bucket || ""
      });
    }
  }, [settings]);

  const updateSetting = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: any }) => {
      return apiRequest("PATCH", `/api/admin/settings/${key}`, { value });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/settings"] });
    },
    onError: (error: any) => {
      toast({
        title: "Update Failed",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const handleChange = (key: keyof RAGSettings, value: any) => {
    setFormData({
      ...formData,
      [key]: value
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    const promises = Object.keys(formData).map((key) => {
      const typedKey = key as keyof RAGSettings;
      const currentValue = formData[typedKey];
      
      // Skip if value hasn't changed from settings
      if (currentValue === settings?.[typedKey]) {
        return Promise.resolve();
      }
      
      // Skip password fields if they're still showing masked values (********)
      if ((typedKey === 'openai_api_key' || 
           typedKey === 'aws_access_key_id' || 
           typedKey === 'aws_secret_access_key') && 
          typeof currentValue === 'string' && 
          currentValue.match(/^\*+$/)) {
        return Promise.resolve();
      }
      
      return updateSetting.mutateAsync({
        key: typedKey,
        value: currentValue
      });
    });

    try {
      await Promise.all(promises);
      toast({ title: "RAG Settings Updated Successfully" });
      setHasChanges(false);
    } catch (error) {
      // Error already handled by mutation
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card id="rag-configuration">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5" />
          RAG Configuration
        </CardTitle>
        <CardDescription>
          Configure RAG (Retrieval-Augmented Generation) embeddings provider and settings for knowledge base
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* RAG Provider Selection */}
        <div>
          <div className="flex items-center">
            <Label>RAG Provider</Label>
            <InfoTooltip content="Choose between OpenAI (simpler, higher cost) or AWS Bedrock (95% cheaper, requires AWS setup)" />
          </div>
          <Select
            value={formData.rag_provider || "openai"}
            onValueChange={(value) => handleChange("rag_provider", value)}
          >
            <SelectTrigger data-testid="select-rag-provider">
              <SelectValue placeholder="Select RAG provider" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="openai">OpenAI (Standard)</SelectItem>
              <SelectItem value="bedrock">AWS Bedrock (95% cost savings)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">
            OpenAI: $0.020/1M tokens | AWS Bedrock: $0.0001/1M tokens (200x cheaper)
          </p>
        </div>

        {/* OpenAI Configuration (shown when provider is openai) */}
        {formData.rag_provider === "openai" && (
          <div className="p-4 border rounded-lg bg-muted/30">
            <h4 className="font-medium mb-3">OpenAI Configuration</h4>
            <div>
              <div className="flex items-center">
                <Label>OpenAI API Key</Label>
                <InfoTooltip content="API key from OpenAI platform for embeddings generation" />
              </div>
              <Input
                type="password"
                value={formData.openai_api_key || ""}
                onChange={(e) => handleChange("openai_api_key", e.target.value)}
                placeholder="sk-..."
                data-testid="input-openai-key"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Get your API key from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="underline">OpenAI Platform</a>
              </p>
            </div>
          </div>
        )}

        {/* AWS Bedrock Configuration (shown when provider is bedrock) */}
        {formData.rag_provider === "bedrock" && (
          <div className="p-4 border rounded-lg bg-muted/30 space-y-4">
            <h4 className="font-medium">AWS Bedrock Configuration</h4>
            
            <div>
              <div className="flex items-center">
                <Label>AWS Region (for S3 storage)</Label>
                <InfoTooltip content="AWS region for S3 storage and other services (can be different from Bedrock region)" />
              </div>
              <Input
                value={formData.aws_region || ""}
                onChange={(e) => handleChange("aws_region", e.target.value)}
                placeholder="ap-southeast-2"
                data-testid="input-aws-region"
              />
              <p className="text-xs text-muted-foreground mt-1">
                This can be your preferred region for S3 and other AWS services
              </p>
            </div>

            <div>
              <div className="flex items-center">
                <Label>Bedrock Region (for embeddings) *</Label>
                <InfoTooltip content="AWS region for Bedrock Titan models. MUST be one of: us-east-1, us-west-2, eu-west-1, ap-northeast-1" />
              </div>
              <Select
                value={formData.bedrock_region || "us-east-1"}
                onValueChange={(value) => handleChange("bedrock_region", value)}
              >
                <SelectTrigger data-testid="select-bedrock-region">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="us-east-1">us-east-1 (N. Virginia) - Recommended</SelectItem>
                  <SelectItem value="us-west-2">us-west-2 (Oregon)</SelectItem>
                  <SelectItem value="eu-west-1">eu-west-1 (Ireland)</SelectItem>
                  <SelectItem value="ap-northeast-1">ap-northeast-1 (Tokyo)</SelectItem>
                  <SelectItem value="ap-southeast-2">ap-southeast-2 (Sydney)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-yellow-600 mt-1">
                ⚠️ Titan models are ONLY available in these 4 regions. If you use a different region for AWS services (like S3), that's fine - just set this to a supported region.
              </p>
            </div>

            <div>
              <div className="flex items-center">
                <Label>AWS Access Key ID</Label>
                <InfoTooltip content="AWS access key with Bedrock permissions" />
              </div>
              <Input
                type="password"
                value={formData.aws_access_key_id || ""}
                onChange={(e) => handleChange("aws_access_key_id", e.target.value)}
                placeholder="AKIA..."
                data-testid="input-aws-access-key"
              />
            </div>

            <div>
              <div className="flex items-center">
                <Label>AWS Secret Access Key</Label>
                <InfoTooltip content="AWS secret access key for Bedrock access" />
              </div>
              <Input
                type="password"
                value={formData.aws_secret_access_key || ""}
                onChange={(e) => handleChange("aws_secret_access_key", e.target.value)}
                placeholder="..."
                data-testid="input-aws-secret-key"
              />
            </div>

            <div>
              <div className="flex items-center">
                <Label>Bedrock Embedding Model</Label>
                <InfoTooltip content="Embedding model for converting text to vectors" />
              </div>
              <Select
                value={formData.bedrock_embedding_model || "amazon.titan-embed-text-v2:0"}
                onValueChange={(value) => handleChange("bedrock_embedding_model", value)}
              >
                <SelectTrigger data-testid="select-bedrock-embedding-model">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="amazon.titan-embed-text-v2:0">Titan Embed Text v2 (1024 dim)</SelectItem>
                  <SelectItem value="amazon.titan-embed-text-v1">Titan Embed Text v1 (1536 dim)</SelectItem>
                  <SelectItem value="cohere.embed-english-v3">Cohere Embed English v3</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <div className="flex items-center">
                <Label>Bedrock Reranking Model (Optional)</Label>
                <InfoTooltip content="Model for improving search result relevance" />
              </div>
              <Input
                value={formData.bedrock_reranking_model || ""}
                onChange={(e) => handleChange("bedrock_reranking_model", e.target.value)}
                placeholder="amazon.nova-pro-v1:0"
                data-testid="input-bedrock-reranking-model"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Leave empty to disable reranking
              </p>
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                Make sure Bedrock is enabled in your AWS region and you have model access enabled in AWS Console
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* S3 Recording Bucket Configuration */}
        <div className="p-4 border rounded-lg bg-muted/30 space-y-4">
          <h4 className="font-medium flex items-center gap-2">
            <HardDrive className="h-4 w-4" />
            S3 Recording Storage
          </h4>

          <div>
            <div className="flex items-center">
              <Label>S3 Recording Bucket Name</Label>
              <InfoTooltip content="S3 bucket name for storing call recordings. Uses the AWS credentials configured above. Leave empty to use default environment-based bucket names." />
            </div>
            <Input
              value={formData.s3_recording_bucket || ""}
              onChange={(e) => handleChange("s3_recording_bucket", e.target.value)}
              placeholder="xevoicepro-recordings-prod"
              data-testid="input-s3-recording-bucket"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Default buckets: <code>xevoicepro-recordings-local</code> (local), <code>xevoicepro-recordings-dev</code> (dev), <code>xevoicepro-recordings</code> (prod)
            </p>
          </div>

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-sm">
              Uses the same AWS credentials as configured above. Make sure the bucket exists and the AWS user has write permissions to it.
            </AlertDescription>
          </Alert>
        </div>

        <div className="flex justify-end pt-4">
          <Button
            onClick={handleSave}
            disabled={!hasChanges || updateSetting.isPending}
            data-testid="button-save-rag-settings"
          >
            {updateSetting.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save RAG Settings
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}